import { RESULTS_DESTINATION_LABEL, type ResultsDestination } from '@/api/integrationsApi'
import type { LisResultRelease, LisReleaseStatus } from '@/api/lisResultsApi'

/**
 * What a release of results to Epic came to, in the words the screen uses
 * (z8nrz7ffzv AC3.4, AC3.6). Kept here, not in the component, so every outcome
 * is tested.
 */

export type ReleaseAction = 'retry' | 'send-again' | null

export interface ReleaseStatusView {
  text: string
  tone: 'success' | 'error' | 'warning' | 'muted'
  /**
   * Retry: not delivered, may have reached Epic, same release.
   * Send again: not set up, nothing was sent, same release once the channel exists.
   */
  action: ReleaseAction
}

export function destinationLabel(destination: string): string {
  return RESULTS_DESTINATION_LABEL[destination as ResultsDestination] ?? destination
}

/**
 * A `pending` release older than this never had its answer recorded; Retry
 * may pick it up (the relay is idempotent on the releaseId). Matches the
 * backend's STALE_PENDING_MS.
 */
export const STALE_PENDING_MS = 5 * 60_000

export function releaseStatusView(
  release: Pick<LisResultRelease, 'status' | 'destination' | 'reason' | 'duplicate'> & { releasedAt?: string },
  now: number = Date.now(),
): ReleaseStatusView {
  const to = destinationLabel(release.destination)
  const stale =
    release.status === 'pending' && !!release.releasedAt && now - Date.parse(release.releasedAt) > STALE_PENDING_MS
  const views: Record<LisReleaseStatus, ReleaseStatusView> = {
    accepted: {
      text: `Released to ${to}${release.duplicate ? ' (already held; not sent again)' : ''}`,
      tone: 'success',
      action: null,
    },
    rejected: {
      text: `Rejected by ${to}${release.reason ? `: ${release.reason}` : ''}`,
      tone: 'error',
      action: null,
    },
    undelivered: {
      text: `Not delivered to ${to}${release.reason ? `: ${release.reason}` : ''}`,
      tone: 'warning',
      action: 'retry',
    },
    not_set_up: {
      text: `Not sent: ${to} results aren't set up yet`,
      tone: 'warning',
      action: 'send-again',
    },
    pending: stale
      ? { text: `No answer recorded from ${to}`, tone: 'warning', action: 'retry' }
      : { text: `Sending to ${to}…`, tone: 'muted', action: null },
    // A PDF release whose file changed before it could be sent again: closed,
    // so a new release sends the current file (z8nrz7fgcp).
    cancelled: {
      text: `Cancelled${release.reason ? `: ${release.reason}` : ''}`,
      tone: 'muted',
      action: null,
    },
  }
  return views[release.status] ?? { text: release.status, tone: 'muted', action: null }
}

/** The line above the preview (ffzv AC3.3, fgcp AC2.1). */
export function releaseDestinationNote(destination: string, what: 'results' | 'pdf' = 'results'): string {
  return `${what === 'pdf' ? 'This PDF' : 'These results'} will be sent to ${destinationLabel(destination)}.`
}

/** Statuses that are still open: sent again, never released anew. Must match the backend's OPEN_STATUSES. */
export function isOpenRelease(status: LisReleaseStatus): boolean {
  return status === 'pending' || status === 'undelivered' || status === 'not_set_up'
}

export const RESULT_STATUS_LABEL: Record<string, string> = {
  F: 'Final',
  P: 'Preliminary',
  C: 'Corrected',
}
